import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Spin,
  Select,
  Upload,
  Image,
  Popconfirm
} from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../constants/Constants";

const { Option } = Select;

const UpdateProductForm = ({
  visible,
  onClose,
  product,
  onUpdate,
  setUpdatedBoolean,
  updatedBoolean,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  
  // Data States
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  
  // Image States
  const [fileList, setFileList] = useState([]); 
  const [existingPhotos, setExistingPhotos] = useState([]);

  // =========================================================
  // 1. INITIALIZATION
  // =========================================================
  useEffect(() => {
    const initializeForm = async () => {
        if (!visible || !product) return;

        try {
            // Reset UI
            setFileList([]);
            setExistingPhotos([]); 
            setSubcategories([]); 
            
            // 1. Load Photos
            fetchExistingPhotos(product.productId);

            // 2. Fetch Categories
            const catRes = await axios.get(`${BASE_URL}/category/all-unhide`);
            const catList = catRes.data || [];
            setCategories(catList);

            // 3. Find Active Category
            let matchedCategory = catList.find(c => 
                (c.categoryName || "").trim().toLowerCase() === (product.productCategory || product.category || "").trim().toLowerCase()
            );

            if (!matchedCategory && product.categoryId) {
                matchedCategory = catList.find(c => c.categoryId == product.categoryId);
            }

            let currentSubList = [];

            // 4. Fetch Subcategories
            if (matchedCategory && matchedCategory.categoryId) {
                const subRes = await axios.get(`${BASE_URL}/api/subcategories/by-category/${matchedCategory.categoryId}`);
                currentSubList = subRes.data || [];
                setSubcategories(currentSubList);
            }

            // 5. Determine Subcategory Name to Display
            let subNameToDisplay = product.productSubcategory || product.subCategoryName;

            if (!subNameToDisplay && product.subcategoryId) {
                const idVal = product.subcategoryId;
                if (typeof idVal === 'string' && isNaN(idVal)) {
                    subNameToDisplay = idVal;
                } 
                else {
                    const foundSub = currentSubList.find(s => s.subCategoryId == idVal);
                    if (foundSub) {
                        subNameToDisplay = foundSub.subCategoryName;
                    }
                }
            }

            // 6. Set Form Values
            form.setFieldsValue({
                productId: product.productId,
                productName: product.productName,
                productCategory: matchedCategory ? matchedCategory.categoryName : (product.productCategory || product.category),
                productSubcategory: subNameToDisplay, 
                
                productBrand: product.productBrand || product.brand,
                productShortDescription: product.shortDesc || product.productShortDescription,
                productDescription: product.prdDesc || product.productDescription,
                productRate: product.productRate || product.productPrice, 
                productMRP: product.productMRP || product.mrp,
                productStock: product.productStock,
                productQuantity: product.productQuantity,
                
                // We don't need 'priority' in the form anymore, but we keep it in 'product' obj
                
                // Initialize Shipping Charge
                shippingCharge: product.shippingCharge !== undefined && product.shippingCharge !== null ? product.shippingCharge : 0,
            });

        } catch (err) {
            console.error("Error initializing form:", err);
        }
    };

    initializeForm();
  }, [visible, product, form]);

  // =========================================================
  // 2. HELPER FUNCTIONS
  // =========================================================
  const fetchExistingPhotos = async (id) => {
      try {
          const res = await axios.get(`${BASE_URL}/productphotos/by-product/${id}`);
          setExistingPhotos(res.data || []);
      } catch (err) {
          console.error("Could not fetch photos", err);
      }
  };

  const handleDeletePhoto = async (photoId) => {
      try {
          await axios.delete(`${BASE_URL}/productphotos/delete/${photoId}`);
          message.success("Image deleted");
          setExistingPhotos(prev => prev.filter(p => p.photoId !== photoId));
      } catch (err) {
          message.error("Failed to delete image");
      }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleCategoryChange = async (categoryName) => {
    const matchedCategory = categories.find((c) => c.categoryName === categoryName);
    
    if (matchedCategory && matchedCategory.categoryId) {
        try {
            const res = await axios.get(`${BASE_URL}/api/subcategories/by-category/${matchedCategory.categoryId}`);
            setSubcategories(res.data || []);
        } catch (error) {
            setSubcategories([]);
        }
    } else {
        setSubcategories([]);
    }
    form.setFieldsValue({ productSubcategory: null });
  };

  // =========================================================
  // 3. SUBMIT UPDATE
  // =========================================================
  const handleUpdateProduct = async () => {
    setIsLoading(true);
    try {
      const values = await form.validateFields();
      const productId = product.productId;

      const selectedCategoryObj = categories.find(
        (c) => c.categoryName === values.productCategory
      );

      let finalSubId = null;
      const selectedSubCatObj = subcategories.find(
             s => s.subCategoryName === values.productSubcategory
      );

      if (selectedSubCatObj) {
          finalSubId = selectedSubCatObj.subCategoryId;
      } else {
          if (values.productSubcategory === product.productSubcategory || 
              values.productSubcategory === product.subcategoryId) { 
               if (!isNaN(product.subcategoryId)) {
                   finalSubId = product.subcategoryId;
               } 
          }
      }

      // Helper to safely parse numbers
      const safeParse = (val) => {
          if(!val) return 0;
          const parsed = parseFloat(val);
          return isNaN(parsed) ? 0 : parsed;
      };

      const payload = {
          ...values,
          categoryId: selectedCategoryObj ? selectedCategoryObj.categoryId : product.categoryId,
          category: values.productCategory,
          subcategoryId: finalSubId, 
          
          productPrice: safeParse(values.productRate),
          mrp: safeParse(values.productMRP),
          
          // ➤ CHANGED: PRESERVE EXISTING PRIORITY (Don't read from form)
          priority: product.priority || 0,

          brand: values.productBrand,
          shortDesc: values.productShortDescription,
          prdDesc: values.productDescription,
          
          shippingCharge: safeParse(values.shippingCharge),
      };

      console.log("Updating Payload:", payload);

      // 1. Update Product Data
      await axios.put(`${BASE_URL}/products/update/${productId}`, payload);

      // 2. Upload New Images (if any)
      if (fileList.length > 0) {
          const formData = new FormData();
          fileList.forEach(file => {
              formData.append("photos", file.originFileObj);
          });
          
          await axios.post(`${BASE_URL}/productphotos/upload/${productId}`, formData, {
               headers: { 'Content-Type': 'multipart/form-data' }
          });
          message.success("Product and new images updated successfully!");
      } else {
          message.success("Product updated successfully");
      }

      setUpdatedBoolean(!updatedBoolean);
      onClose();

    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.message || "Failed to update product.";
      message.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title="Update Product & Images"
      visible={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="update" type="primary" onClick={handleUpdateProduct}>
          {isLoading ? <Spin /> : "Update Product"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <div style={{ display: 'flex', gap: '20px' }}>
            <Form.Item style={{ flex: 1 }} label="Product Id" name="productId">
              <Input disabled /> 
            </Form.Item>
            <Form.Item style={{ flex: 1 }} label="Product Name" name="productName">
              <Input />
            </Form.Item>
        </div>

        {existingPhotos.length > 0 && (
            <div className="mb-4 p-4 bg-white border rounded shadow-sm">
                <h4 className="text-sm font-bold mb-3 text-gray-700">Existing Images</h4>
                <div className="flex flex-wrap gap-4">
                    {existingPhotos.map((photo) => (
                        <div key={photo.photoId} className="relative group border rounded p-1">
                            <Image width={80} height={80} src={photo.photoName} className="object-cover rounded"/>
                            <Popconfirm title="Delete?" onConfirm={() => handleDeletePhoto(photo.photoId)}>
                                <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} size="small" className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100"/>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>
        )}

        <div className="p-4 mb-4 bg-gray-50 border rounded border-dashed border-gray-300">
            <Form.Item label="Add New Images">
                <Upload listType="picture-card" fileList={fileList} onChange={handleFileChange} beforeUpload={() => false} multiple={true} accept="image/*">
                    {fileList.length >= 8 ? null : <div><UploadOutlined /><div style={{ marginTop: 8 }}>Upload</div></div>}
                </Upload>
            </Form.Item>
        </div>

        {/* ➤ PRIORITY FIELD REMOVED FROM HERE */}
        
        <Form.Item label="Key Features" name="productShortDescription"><Input.TextArea rows={4} /></Form.Item>
        <Form.Item label="Detailed Description" name="productDescription"><Input.TextArea rows={5} /></Form.Item>

        <div style={{ display: 'flex', gap: '20px' }}>
            <Form.Item style={{ flex: 1 }} label="Category" name="productCategory" rules={[{ required: true }]}>
                <Select onChange={handleCategoryChange}>
                    {categories.map((c) => (<Option key={c.categoryId} value={c.categoryName}>{c.categoryName}</Option>))}
                </Select>
            </Form.Item>

            <Form.Item style={{ flex: 1 }} label="Subcategory" name="productSubcategory">
                <Select 
                    onChange={(v) => form.setFieldsValue({ productSubcategory: v })}
                    placeholder="Select Subcategory"
                >
                    {subcategories.map((s) => (
                        <Option key={s.subCategoryId} value={s.subCategoryName}>{s.subCategoryName}</Option>
                    ))}
                </Select>
            </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: '20px' }}>
            <Form.Item style={{ flex: 1 }} label="Selling Price" name="productRate"><Input type="number"/></Form.Item>
            <Form.Item style={{ flex: 1 }} label="MRP" name="productMRP"><Input type="number"/></Form.Item>
            <Form.Item style={{ flex: 1 }} label="Stock" name="productStock"><Input type="number"/></Form.Item>
        </div>
        
        <div style={{ display: 'flex', gap: '20px' }}>
            <Form.Item style={{ flex: 1 }} label="Brand" name="productBrand"><Input /></Form.Item>
            <Form.Item style={{ flex: 1 }} label="Pack Quantity" name="productQuantity"><Input type="number" /></Form.Item>

            <Form.Item style={{ flex: 1 }} label="Shipping Charge (₹)" name="shippingCharge" initialValue={0}>
                <Input type="number" placeholder="0" />
            </Form.Item>
        </div>

      </Form>
    </Modal>
  );
};

export default UpdateProductForm;